import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationError } from 'webapp-types';

import setField from 'webapp/helpers/set-field';
import tagColor from 'webapp/helpers/tag-color';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormErrorComponent from 'webapp/components/form-error';
import FormInputComponent from 'webapp/components/form/input';
import TagPopupTagColorsComponent from 'webapp/components/tag/popup-tag-colors';

import type Tag from 'webapp/models-custom/tag';

import type CurrentUser from 'webapp/services/current-user';

export interface Args {
	model: Tag;
	isDisabled: boolean;
	onDelete: (tag: Tag) => void;
	validateTagsNames: () => boolean;
}

export default class TagEditComponent extends Component<Args> {
	@service declare currentUser: CurrentUser;
	@service('popup') declare popupService: Service.Popup;

	@tracked confirmingDelete = false;

	get errors(): ValidationError[] {
		return this.args.model.errorsArray.map((error) => {
			const copy = Object.assign({}, error);
			copy.name = 'general'; // FormError component only shows general errors
			return copy;
		});
	}

	get canEditAndDelete(): boolean {
		return (
			this.args.model.isNew ||
			this.currentUser.isAdmin ||
			this.currentUser.model?.id === String(this.args.model.createdBy)
		);
	}

	get isDisabled(): boolean {
		return !this.canEditAndDelete;
	}

	@action
	triggerValidate(): void {
		if (!this.args.validateTagsNames()) {
			return;
		}

		this.args.model.validate();
	}

	@action
	showAvailableColors(e: PointerEvent): void {
		if (this.isDisabled) {
			return;
		}

		const node = e.target as HTMLElement,
			popupAttachTo = node.closest<HTMLElement>('.color .select2')!,
			isPopupOpen = this.popupService.isOpen(TagPopupTagColorsComponent);

		if (!isPopupOpen && popupAttachTo) {
			const popup = this.popupService.createContext(
				TagPopupTagColorsComponent,
				popupAttachTo,
				{
					popupClass: 'popup-type-tag-colors',
					allowStacked: true,
				},
			);
			popup.open({
				onChoice: (color: string) => {
					this.args.model.color = color;
				},
			});
		}
	}

	@action
	confirmYes(): void {
		this.args.onDelete(this.args.model);
		this.args.validateTagsNames();
	}

	@action
	cancelConfirm(): void {
		this.confirmingDelete = false;
	}

	@action
	confirmDelete(): void {
		this.confirmingDelete = true;
	}
	<template>
		<li data-id={{@model.id}}>
			<FormErrorComponent @errors={{this.errors}} />

			<div class="name">
				<FormInputComponent
					name="name"
					maxlength="35"
					disabled={{this.isDisabled}}
					@value={{@model.name}}
					@focusOut={{this.triggerValidate}}
					@loseFocusOnEnter={{true}}
					@onEmpty={{setField target=@model property="name"}}
				/>
			</div>
			<div class="color">
				<div class="ct">
					<span
						role="button"
						class="select2 select2-container"
						{{on "click" this.showAvailableColors}}
					>
						<span class="selection">
							<span class="select2-selection select2-selection--single">
								<span class="select2-selection__rendered">
									<div>
										<span>
											<CommonUiIconComponent
												@name="TagFill"
												@size="xs"
												@fill={{tagColor @model.color}}
											/>
										</span>
									</div>
								</span>
								<span class="select2-selection__arrow" role="presentation">
									<CommonUiIconComponent @name="ChevronDown" @size="xs" />
								</span>
							</span>
						</span>
					</span>
				</div>
				<label>Color</label>
			</div>
			{{#if this.canEditAndDelete}}
				<div class="delete">
					{{#if this.confirmingDelete}}
						<a role="button" class="confirm-yes" {{on "click" this.confirmYes}}>
							<CommonUiIconComponent @name="Trash" @size="sm" />
							<span class="underline">Confirm</span>
						</a>
						<a role="button" class="confirm-no" {{on "click" this.cancelConfirm}}>
							Cancel
						</a>
					{{else}}
						<a role="button" {{on "click" this.confirmDelete}}>
							<CommonUiIconComponent @name="Trash" @size="sm" />
						</a>
					{{/if}}
				</div>
			{{/if}}

		</li>
	</template>
}
